import { supabase } from "@/integrations/supabase/client";
import { loadTallivoCatalog } from "@/lib/tallivoCatalog";

export type SearchCategory =
  | "articles"
  | "products"
  | "books"
  | "activities"
  | "pages";

export interface SearchResult {
  id: string;
  title: string;
  description: string | null;
  href: string;
  category: SearchCategory;
  image: string | null;
}

export const CATEGORY_LABELS: Record<SearchCategory | "all", string> = {
  all: "All",
  articles: "Articles",
  products: "Products",
  books: "Books",
  activities: "Activities",
  pages: "Pages",
};

export const CATEGORY_ORDER: SearchCategory[] = [
  "articles",
  "products",
  "books",
  "activities",
  "pages",
];

/* ----------------------------- Static catalog ---------------------------- */

interface StaticEntry {
  id: string;
  title: string;
  description: string;
  href: string;
  category: SearchCategory;
  keywords?: string;
}

const STATIC_ENTRIES: StaticEntry[] = [
  // Activities / experiences
  { id: "act-balm-bond", title: "Balm & Bond", description: "Hands-on botanical balm-making experience in St. Kitts.", href: "/experiences/balm-and-bond", category: "activities", keywords: "workshop experience class bonding skincare" },
  { id: "act-creative", title: "Creative Immersion Summer Camp", description: "A summer camp blending art, craft, and Caribbean culture.", href: "/experiences/creative-immersion", category: "activities", keywords: "camp kids children art summer workshop" },
  { id: "act-sunrise", title: "Sunrise & Stillness", description: "A botanical self-care retreat and morning ritual experience.", href: "/experiences/sunrise-and-stillness", category: "activities", keywords: "retreat wellness meditation yoga ritual" },
  { id: "act-events", title: "Events & Pop-Ups", description: "Upcoming Sugar Town Organics events and pop-up markets.", href: "/events", category: "activities", keywords: "event popup market fair" },
  { id: "act-merch", title: "Merch & Lifestyle", description: "Branded lifestyle merchandise from Sugar Town Organics.", href: "/merch", category: "activities", keywords: "merchandise apparel lifestyle" },
  // Brands
  { id: "pg-yaphene", title: "Yaphene", description: "Caribbean botanical hair care.", href: "/brands/yaphene", category: "pages", keywords: "brand hair care botanical" },
  { id: "pg-marapa", title: "Marapa Skincare", description: "Botanical skincare crafted in St. Kitts.", href: "/brands/marapa", category: "pages", keywords: "brand skincare" },
  { id: "pg-nezer", title: "Nezer Men Skincare", description: "Skincare for men.", href: "/brands/nezer", category: "pages", keywords: "brand men skincare grooming" },
  { id: "pg-calume", title: "Calumé", description: "Botanical fragrance and body care.", href: "/brands/calume", category: "pages", keywords: "brand fragrance body" },
  { id: "pg-flauriel", title: "Flauriel Foods", description: "Caribbean foods, sauces, and preserves.", href: "/brands/flauriel", category: "pages", keywords: "brand food sauces preserves hot sauce" },
  { id: "pg-herbz", title: "Herbz Kulture", description: "Herbal teas and botanicals.", href: "/brands/herbz-kulture", category: "pages", keywords: "brand tea herbal" },
  { id: "pg-tallivo", title: "Tallivo", description: "POS commerce and family financial literacy tools.", href: "/brands/tallivo", category: "pages", keywords: "brand pos finance" },
  { id: "pg-anaelli", title: "AnaElli Digital", description: "Digital journals, workbooks, and Caribbean reads.", href: "/brands/anaelli", category: "pages", keywords: "brand digital books journals" },
  // Guides & key pages
  { id: "pg-haircare-guide", title: "Hair Care Guide", description: "Natural hair care for Black women.", href: "/natural-hair-care-black-women", category: "pages", keywords: "guide hair" },
  { id: "pg-skincare-guide", title: "Skincare Guide", description: "Caribbean botanical skincare guide.", href: "/caribbean-botanical-skincare", category: "pages", keywords: "guide skincare" },
  { id: "pg-shop-concern", title: "Shop by Concern", description: "Find products by skin and hair concern.", href: "/shop-by-concern", category: "pages", keywords: "shop concern" },
  { id: "pg-longevity", title: "Caribbean Longevity Method", description: "The Caribbean Heritage Longevity Method.", href: "/caribbean-heritage-longevity-method", category: "pages", keywords: "longevity wellness method" },
  { id: "pg-shop", title: "Shop", description: "Browse all handcrafted botanical products.", href: "/shop", category: "pages", keywords: "shop store products" },
  { id: "pg-our-story", title: "Our Story", description: "The story of Sugar Town Organics.", href: "/our-story", category: "pages", keywords: "about story" },
  { id: "pg-legacy", title: "Legacy & Impact", description: "Education, entrepreneurship, and community impact.", href: "/legacy-impact", category: "pages", keywords: "legacy impact community education" },
  { id: "pg-wholesale", title: "Wholesale", description: "Wholesale and export with Di Organics Shoppe.", href: "/wholesale", category: "pages", keywords: "wholesale export b2b" },
  { id: "pg-library", title: "Island Library", description: "Books, journals, and keepsakes.", href: "/island-library", category: "pages", keywords: "books library reads" },
];

function searchStatic(term: string): SearchResult[] {
  const t = term.toLowerCase();
  return STATIC_ENTRIES.filter(
    (e) =>
      e.title.toLowerCase().includes(t) ||
      e.description.toLowerCase().includes(t) ||
      (e.keywords ? e.keywords.toLowerCase().includes(t) : false)
  ).map(({ keywords, ...rest }) => ({ ...rest, image: null }));
}

/* ------------------------------- Articles -------------------------------- */

async function searchArticles(term: string): Promise<SearchResult[]> {
  // Server-side full-text search (ranked, GIN-indexed) via RPC.
  const { data, error } = await (supabase as any).rpc("search_articles", {
    q: term,
    max_results: 8,
  });
  if (error || !data) return [];
  return (data as any[]).map((r) => ({
    id: `article-${r.id}`,
    title: r.title,
    description: r.summary,
    href: `/blog/${r.slug}`,
    category: "articles" as const,
    image: r.image_url ?? null,
  }));
}

/* -------------------------------- Books ---------------------------------- */

async function searchBooks(term: string): Promise<SearchResult[]> {
  const escaped = term.replace(/[%,()]/g, " ");
  const { data, error } = await (supabase as any)
    .from("digital_products_public")
    .select("id,slug,title,short_description,cover_image_url,is_active")
    .eq("is_active", true)
    .or(`title.ilike.%${escaped}%,short_description.ilike.%${escaped}%`)
    .limit(8);
  if (error || !data) return [];
  return data.map((r: any) => ({
    id: `book-${r.id}`,
    title: r.title,
    description: r.short_description,
    href: `/brands/anaelli#${r.slug}`,
    category: "books" as const,
    image: r.cover_image_url ?? null,
  }));
}

/* ------------------------------- Products -------------------------------- */

async function searchProducts(term: string): Promise<SearchResult[]> {
  try {
    const t = term.toLowerCase();
    const all = await loadTallivoCatalog();
    const edges = all
      .filter((p) => `${p.node.title} ${p.node.description} ${p.categories.join(" ")}`.toLowerCase().includes(t))
      .slice(0, 8);
    return edges.map((e) => ({
      id: `product-${e.node.id}`,
      title: e.node.title,
      description: e.node.description?.slice(0, 140) ?? null,
      href: `/product/${e.node.handle}`,
      category: "products" as const,
      image: e.node.images?.edges?.[0]?.node?.url ?? null,
    }));
  } catch {
    return [];
  }
}

/* ------------------------------ Public API ------------------------------- */

export async function searchSite(term: string): Promise<SearchResult[]> {
  const trimmed = term.trim();
  if (trimmed.length < 2) return [];
  const [articles, books, products] = await Promise.all([
    searchArticles(trimmed),
    searchBooks(trimmed),
    searchProducts(trimmed),
  ]);
  return [...products, ...articles, ...books, ...searchStatic(trimmed)];
}

/** Split text into segments marking the parts that match `term` (case-insensitive). */
export function highlightSegments(
  text: string,
  term: string
): { text: string; match: boolean }[] {
  const trimmed = term.trim();
  if (!trimmed) return [{ text, match: false }];
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${escaped})`, "ig");
  const parts = text.split(re);
  return parts
    .filter((p) => p !== "")
    .map((p) => ({ text: p, match: p.toLowerCase() === trimmed.toLowerCase() }));
}
